import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { contentApi, ScopeBadge, scopePath } from "./contentApi";
import ContentConfirm from "./ContentConfirm";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const emptyForm = () => ({
  question_text: "",
  options: ["", ""],
  correct_answer: "",
  category: "",
  explanation: "",
});

export default function SltQuestionEditor({
  scope,
  onMessage,
  onDirtyChange,
  onBusyChange,
}) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [reconcilePending, setReconcilePending] = useState(false);
  const [deactivateRow, setDeactivateRow] = useState(null);
  const [pendingFormDiscard, setPendingFormDiscard] = useState(null);
  const generation = useRef(0);
  const busyRef = useRef(false);
  const abortRef = useRef(null);
  const [total, setTotal] = useState(0);
  const { query, update: updateQuery, reset: resetQuery } = useAdminListQuery();

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(
    () => onBusyChange(busy || reconcilePending),
    [busy, reconcilePending, onBusyChange],
  );

  async function loadRows({ postCommit = false, reconcile = false } = {}) {
    const current = ++generation.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    if (postCommit || reconcile) setReconcilePending(true);
    else setReconcilePending(false);
    if (!postCommit) setError("");
    try {
      const result = await contentApi(
        `/admin/content/slt-questions?organisation_id=${scopePath(scope)}&paged=true&limit=100${query.q ? `&q=${encodeURIComponent(query.q)}` : ""}${query.status ? `&status=${encodeURIComponent(query.status)}` : ""}${query.category ? `&category=${encodeURIComponent(query.category)}` : ""}${query.offset ? `&offset=${encodeURIComponent(query.offset)}` : ""}`,
        { signal: controller.signal },
      );
      if (current !== generation.current) return true;
      setRows(result.items || result);
      setTotal(Number(result.total) || (Array.isArray(result) ? result.length : 0));
      setRefreshError("");
      setReconcilePending(false);
      return true;
    } catch (e) {
      if (e.name === "AbortError" || current !== generation.current)
        return false;
      setRefreshError(
        postCommit ? `Saved, but refresh failed: ${e.message}` : e.message,
      );
      setReconcilePending(postCommit || reconcile);
      return false;
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  useLayoutEffect(() => {
    setRows([]);
    setEditing(null);
    setForm(emptyForm());
    setDirty(false);
    loadRows();
    return () => {
      generation.current += 1;
      abortRef.current?.abort();
    };
  }, [scope, query.q, query.status, query.category, query.offset]);

  function updateForm(key, value) {
    setForm((old) => ({ ...old, [key]: value }));
    setDirty(true);
  }

  function updateOption(index, value) {
    setForm((old) => ({
      ...old,
      options: old.options.map((item, i) => (i === index ? value : item)),
    }));
    setDirty(true);
  }

  function reset() {
    setForm(emptyForm());
    setEditing(null);
    setDirty(false);
  }

  function edit(row) {
    setEditing(row);
    setForm({
      question_text: row.question_text,
      options: [...row.options],
      correct_answer: row.correct_answer,
      category: row.category,
      explanation: row.explanation || "",
    });
    setDirty(false);
  }

  function requestEdit(row) {
    if (dirty) setPendingFormDiscard({ row });
    else edit(row);
  }

  function requestReset() {
    if (dirty) setPendingFormDiscard({ reset: true });
    else reset();
  }

  function confirmFormDiscard() {
    const pending = pendingFormDiscard;
    setPendingFormDiscard(null);
    if (pending?.reset) reset();
    else if (pending?.row) edit(pending.row);
  }

  async function save(event) {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const requestScope = scope;
    const requestGeneration = generation.current;
    const editingId = editing?.id;
    const body = {
      ...form,
      options: form.options.filter(Boolean),
      organisation_id: requestScope === "global" ? null : requestScope,
    };
    try {
      const row = editingId
        ? await contentApi(
            `/admin/content/slt-questions/${editingId}?organisation_id=${scopePath(requestScope)}`,
            {
              method: "PATCH",
              body: (({ organisation_id: _ignored, ...patch }) => patch)(body),
            },
          )
        : await contentApi("/admin/content/slt-questions", {
            method: "POST",
            body,
          });
      if (requestGeneration !== generation.current || requestScope !== scope)
        return;
      setRows((old) =>
        editingId
          ? old.map((item) => (item.id === row.id ? row : item))
          : [row, ...old],
      );
      reset();
      onMessage(editingId ? "Question updated" : "Question added");
      await loadRows({ postCommit: true });
    } catch (e) {
      if (requestGeneration === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function deactivate() {
    const row = deactivateRow;
    setDeactivateRow(null);
    if (!row || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const requestGeneration = generation.current;
    try {
      const result = await contentApi(
        `/admin/content/slt-questions/${row.id}?organisation_id=${scopePath(scope)}`,
        { method: "DELETE" },
      );
      if (requestGeneration !== generation.current) return;
      setRows((old) =>
        old.map((item) =>
          item.id === row.id
            ? result?.id
              ? result
              : { ...item, is_active: false }
            : item,
        ),
      );
      onMessage("Question deactivated");
      await loadRows({ postCommit: true });
    } catch (e) {
      if (requestGeneration === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  return (
    <section
      className="content-editor-section"
      aria-labelledby="slt-editor-title"
    >
      <div className="content-section-heading">
        <div>
          <h2 id="slt-editor-title">SLT question bank</h2>
          <p>
            Manage questions for the selected scope. Deactivation preserves
            response history.
          </p>
        </div>
        <button
          type="button"
          className="content-button secondary"
          onClick={() => loadRows({ reconcile: reconcilePending })}
          disabled={busy || loading || reconcilePending}
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search question text or category" total={total} loading={loading} onSearch={(q) => updateQuery({ q })} onReset={resetQuery}>
        <label className="text-sm font-medium">Status<select aria-label="Filter question status" value={query.status || "all"} onChange={(event) => updateQuery({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <label className="text-sm font-medium">Category<input aria-label="Filter question category" value={query.category || ""} onChange={(event) => updateQuery({ category: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
      </AdminListToolbar>
      {error && (
        <p className="content-error" role="alert">
          {error}
        </p>
      )}
      {refreshError && (
        <p className="content-error" role="alert">
          {refreshError}{" "}
          <button
            type="button"
            className="content-button secondary"
            onClick={() => loadRows({ reconcile: true })}
          >
            Retry refresh
          </button>
        </p>
      )}
      <form className="content-card content-form" onSubmit={save}>
        <fieldset disabled={busy || loading || reconcilePending}>
          <legend>{editing ? "Edit question" : "Add question"}</legend>
          <label>
            Question
            <textarea
              required
              maxLength={2000}
              value={form.question_text}
              onChange={(e) => updateForm("question_text", e.target.value)}
            />
          </label>
          <div className="content-form-grid">
            <label>
              Category
              <input
                required
                maxLength={200}
                value={form.category}
                onChange={(e) => updateForm("category", e.target.value)}
              />
            </label>
            <label>
              Correct answer
              <input
                required
                maxLength={500}
                value={form.correct_answer}
                onChange={(e) => updateForm("correct_answer", e.target.value)}
              />
            </label>
          </div>
          <fieldset>
            <legend>Answer options</legend>
            {form.options.map((option, index) => (
              <div className="content-inline-field" key={`option-${index}`}>
                <input
                  required
                  maxLength={500}
                  aria-label={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                />
                <button
                  type="button"
                  className="content-button icon"
                  onClick={() => {
                    setForm((old) => ({
                      ...old,
                      options: old.options.filter((_, i) => i !== index),
                    }));
                    setDirty(true);
                  }}
                  disabled={form.options.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="content-button secondary"
              onClick={() => {
                setForm((old) => ({ ...old, options: [...old.options, ""] }));
                setDirty(true);
              }}
              disabled={form.options.length >= 20}
            >
              Add option
            </button>
          </fieldset>
          <label>
            Explanation
            <textarea
              maxLength={10000}
              value={form.explanation}
              onChange={(e) => updateForm("explanation", e.target.value)}
            />
          </label>
          <div className="content-actions">
            <button
              className="content-button primary"
              disabled={busy || loading}
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Add question"}
            </button>
            {editing && (
              <button
                type="button"
                className="content-button secondary"
                onClick={requestReset}
                disabled={busy || reconcilePending}
              >
                Cancel
              </button>
            )}
          </div>
        </fieldset>
      </form>
      <div className="content-card">
        <h3>Questions ({rows.length})</h3>
        {rows.length === 0 ? (
          <p className="content-muted">
            {loading ? "Loading questions…" : "No questions in this scope."}
          </p>
        ) : (
          <div className="content-row-list">
            {rows.map((row) => (
              <article
                className={`content-row ${row.is_active ? "" : "inactive"}`}
                key={row.id}
              >
                <div className="content-row-main">
                  <div className="content-row-title">
                    <strong>{row.question_text}</strong>
                    <ScopeBadge row={row} />
                  </div>
                  <p>
                    {row.category} · {row.options.join(" / ")}
                  </p>
                  {!row.is_active && (
                    <span className="content-status">Inactive</span>
                  )}
                </div>
                <div className="content-actions">
                  <button
                    type="button"
                    className="content-button secondary"
                    onClick={() => requestEdit(row)}
                    disabled={busy || reconcilePending || !row.is_active}
                  >
                    Edit
                  </button>
                  {row.is_active && (
                    <button
                      type="button"
                      className="content-button danger"
                      onClick={() => setDeactivateRow(row)}
                      disabled={busy || reconcilePending}
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <AdminListPager offset={Number(query.offset || 0)} limit={100} total={total} disabled={loading || Boolean(busy)} onPage={(offset) => updateQuery({ offset }, { resetOffset: false })} />
      <ContentConfirm
        open={Boolean(deactivateRow)}
        title="Deactivate this question?"
        description="Existing response history is preserved."
        action="Deactivate"
        onConfirm={deactivate}
        onClose={() => setDeactivateRow(null)}
      />
      <ContentConfirm
        open={Boolean(pendingFormDiscard)}
        title="Discard question changes?"
        description="Opening another question or cancelling will discard the current form."
        action="Discard changes"
        onConfirm={confirmFormDiscard}
        onClose={() => setPendingFormDiscard(null)}
      />
    </section>
  );
}
